import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles.css';
import { hasRole, SessionProvider, useMe, useSession } from './lib/session';
import { Shell } from './components/Shell';
import { Login } from './routes/Login';
import { Today } from './routes/Today';
import { Floor } from './routes/Floor';
import { AnalysisEditor, AnalysisView } from './routes/Analysis';
import { PlanDetail, PlanNew } from './routes/Plan';
import { TradeDetailPage, TradesList } from './routes/Trade';
import { RiskDesk } from './routes/RiskDesk';
import { Admin } from './routes/Admin';
import { Markets } from './routes/Markets';
import { Deliveries } from './routes/Deliveries';
import { Reminders } from './routes/Reminders';
import { registerServiceWorker } from './lib/push';
import { Settings } from './routes/Settings';
import { Apply, ApplicationStatusPage } from './routes/Apply';
import { Activate } from './routes/Activate';
import { ForgotPassword, ResetPassword } from './routes/Password';
import { PolicyGate } from './routes/PolicyGate';
import { Enrolment } from './routes/Enrolment';
import { MySymbols, SymbolRequests } from './routes/Symbols';
import { SetupsList } from './routes/Setups';
import { SetupDetailPage } from './routes/SetupDetail';
import { SetupForm } from './routes/SetupForm';
import { HandoverNew, Handovers } from './routes/Handovers';
import { Compliance } from './routes/Compliance';
import { SettingsConsole } from './routes/SettingsConsole';
import { Audit } from './routes/Audit';
import { FxRates } from './routes/FxRates';
import { Library, LibraryItemPage } from './routes/Library';
import { LibraryItemEditor, StrategyEditor } from './routes/LibraryEditor';
import { StrategyPage } from './routes/Strategy';
import { Analytics } from './routes/Analytics';
import { SignalSources } from './routes/SignalSources';
import { RoomPage, RoomsList } from './routes/Rooms';
import { WebinarDetail, WebinarForm, WebinarLive, WebinarsList } from './routes/Webinars';
import { GuestWebinar } from './routes/Guest';
import { Streaming } from './routes/Streaming';
import { PublishingQueue, PublishPackagePage } from './routes/Publishing';

// LiveKit Egress's custom template (INT-STR-03): no session, no Shell, loaded only by that page
const EgressLayout = lazy(() => import('./routes/EgressLayout'));

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true, staleTime: 10_000 } } });

function RequireAuth() {
  const { state } = useSession();
  if (state.status === 'loading') return <div className="p-8 text-sub">Loading…</div>;
  if (state.status === 'offline')
    return (
      <div role="status" className="grid min-h-screen place-items-center p-6 text-center">
        <div className="flex max-w-sm flex-col gap-2">
          <h1 className="m-0 text-xl font-semibold">You’re offline</h1>
          <p className="m-0 text-sub">VTF can’t reach the server right now. You’re still signed in — this page reconnects by itself as soon as the connection is back.</p>
        </div>
      </div>
    );
  if (state.status === 'out') return <Navigate to="/login" replace />;
  return (
    <PolicyGate>
      <Outlet />
    </PolicyGate>
  );
}

/** Staff land on the risk desk; traders on Today. */
function Home() {
  const me = useMe();
  if (!me.data) return null;
  if (!hasRole(me.data.roles, 'TRADER')) return <Navigate to={hasRole(me.data.roles, 'RISK_MANAGER', 'MASTER_TRADER', 'ADMIN') ? '/risk' : '/floor'} replace />;
  return <Today />;
}

function PublicOnly() {
  const { state } = useSession();
  if (state.status === 'in') return <Navigate to="/" replace />;
  return <Login />;
}

const router = createBrowserRouter([
  { path: '/login', element: <PublicOnly /> },
  { path: '/apply', element: <Apply /> },
  { path: '/invite/:token', element: <Apply /> },
  { path: '/apply/status', element: <ApplicationStatusPage /> },
  { path: '/apply/status/:token', element: <ApplicationStatusPage /> },
  { path: '/activate/:token', element: <Activate /> },
  { path: '/g/:token', element: <GuestWebinar /> }, // webinar guests: no sign-in, no Shell (INT-WEB-06)
  { path: '/egress/layout/:key', element: <Suspense fallback={null}><EgressLayout /></Suspense> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password/:token', element: <ResetPassword /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          { index: true, element: <Home /> },
          { path: 'floor', element: <Floor /> },
          { path: 'analysis', element: <AnalysisEditor /> },
          { path: 'analyses/:id', element: <AnalysisView /> },
          { path: 'plans/new', element: <PlanNew /> },
          { path: 'plans/:id', element: <PlanDetail /> },
          { path: 'trades', element: <TradesList /> },
          { path: 'trades/:id', element: <TradeDetailPage /> },
          { path: 'risk', element: <RiskDesk /> },
          { path: 'admin', element: <Admin /> },
          { path: 'admin/markets', element: <Markets /> },
          { path: 'admin/deliveries', element: <Deliveries /> },
          { path: 'reminders', element: <Reminders /> },
          { path: 'library', element: <Library /> },
          { path: 'library/new', element: <LibraryItemEditor /> },
          { path: 'library/items/:id', element: <LibraryItemPage /> },
          { path: 'library/items/:id/edit', element: <LibraryItemEditor /> },
          { path: 'library/strategies/new', element: <StrategyEditor /> },
          { path: 'library/strategies/:code', element: <StrategyPage /> },
          { path: 'library/strategies/:code/edit', element: <StrategyEditor /> },
          { path: 'settings', element: <Settings /> },
          { path: 'symbols', element: <MySymbols /> },
          { path: 'admin/enrolment', element: <Enrolment /> },
          { path: 'admin/symbol-requests', element: <SymbolRequests /> },
          { path: 'setups', element: <SetupsList /> },
          { path: 'setups/new', element: <SetupForm /> },
          { path: 'setups/:id', element: <SetupDetailPage /> },
          { path: 'setups/:id/edit', element: <SetupForm key="edit" /> },
          { path: 'rooms', element: <RoomsList /> },
          { path: 'rooms/:id', element: <RoomPage /> },
          { path: 'webinars', element: <WebinarsList /> },
          { path: 'webinars/new', element: <WebinarForm /> },
          { path: 'webinars/:id', element: <WebinarDetail /> },
          { path: 'webinars/:id/edit', element: <WebinarForm /> },
          { path: 'webinars/:id/live', element: <WebinarLive /> },
          { path: 'handovers', element: <Handovers /> },
          { path: 'handovers/new', element: <HandoverNew /> },
          { path: 'compliance', element: <Compliance /> },
          { path: 'analytics', element: <Analytics /> },
          { path: 'admin/settings', element: <SettingsConsole /> },
          { path: 'admin/audit', element: <Audit /> },
          { path: 'admin/fx', element: <FxRates /> },
          { path: 'admin/signals', element: <SignalSources /> },
          { path: 'admin/streaming', element: <Streaming /> },
          { path: 'publishing', element: <PublishingQueue /> },
          { path: 'publishing/:id', element: <PublishPackagePage /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

registerServiceWorker();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
